"use client";
import React from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { useSearchParams, useRouter } from "next/navigation";
import Button from "@/components/primitives/button";
import Icon from "@/components/primitives/icon";
import CapperReviewCard from "./review-card";
import { CapperReviewModel } from "common";

const formatTimeAgo = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInMilliseconds = now.getTime() - date.getTime();
  const diffInDays = Math.floor(diffInMilliseconds / (1000 * 60 * 60 * 24));

  if (diffInDays === 0) {
    const diffInHours = Math.floor(diffInMilliseconds / (1000 * 60 * 60));
    return diffInHours === 0 ? "Just now" : `${diffInHours} hours ago`;
  } else if (diffInDays === 1) {
    return "1 day ago";
  } else if (diffInDays < 30) {
    return `${diffInDays} days ago`;
  } else if (diffInDays < 365) {
    const diffInMonths = Math.floor(diffInDays / 30);
    return `${diffInMonths} month${diffInMonths > 1 ? "s" : ""} ago`;
  } else {
    const diffInYears = Math.floor(diffInDays / 365);
    return `${diffInYears} year${diffInYears > 1 ? "s" : ""} ago`;
  }
};

const formatMemberSince = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInMilliseconds = now.getTime() - date.getTime();
  const diffInDays = Math.floor(diffInMilliseconds / (1000 * 60 * 60 * 24));

  if (diffInDays < 30) {
    return `${diffInDays} days`;
  } else if (diffInDays < 365) {
    const diffInMonths = Math.floor(diffInDays / 30);
    return `${diffInMonths} month${diffInMonths > 1 ? "s" : ""}`;
  } else {
    const diffInYears = Math.floor(diffInDays / 365);
    return `${diffInYears} year${diffInYears > 1 ? "s" : ""}`;
  }
};

interface ReviewsModalContentProps {
  capperName: string;
  reviewCount: number;
  reviews: CapperReviewModel[];
}

export default function ReviewsModalContent({
  capperName,
  reviewCount,
  reviews,
}: ReviewsModalContentProps) {
  const searchParams = useSearchParams();
  const router = useRouter();
  const isOpen = searchParams.get("reviews") === "true";

  const handleOpenChange = (open: boolean) => {
    const params = new URLSearchParams(searchParams);
    if (open) {
      params.set("reviews", "true");
    } else {
      params.delete("reviews");
    }

    const newUrl = params.toString()
      ? `?${params.toString()}`
      : window.location.pathname;
    router.push(newUrl, { scroll: false });
  };

  const handleButtonClick = () => {
    const params = new URLSearchParams(searchParams);
    params.set("reviews", "true");
    router.push(`?${params.toString()}`, { scroll: false });
  };

  return (
    <>
      {/* Button - only show if there are reviews */}
      {reviewCount > 0 && (
        <div className="place-items-center">
          <Button color="dark" onClick={handleButtonClick}>
            View all {reviewCount} reviews <Icon name="chat-bubble" />
          </Button>
        </div>
      )}

      {/* Modal */}
      <Dialog.Root open={isOpen} onOpenChange={handleOpenChange}>
        <Dialog.Portal>
          <Dialog.Overlay className="z-50 fixed inset-0 bg-black bg-opacity-80 data-[state=closed]:animate-overlayHide data-[state=open]:animate-overlayShow" />
          <Dialog.Content className="top-[50%] left-[50%] z-50 fixed bg-surface-100 shadow-elevation-400 border-2 border-surface-300 rounded-xl focus:outline-none w-[90vw] max-w-[1200px] max-h-[85vh] overflow-hidden translate-x-[-50%] translate-y-[-50%] data-[state=closed]:animate-contentHide data-[state=open]:animate-contentShow">
            <div className="flex flex-col h-full max-h-[85vh]">
              {/* Header */}
              <div className="flex justify-between items-center bg-surface-200 p-6 border-surface-300 border-b">
                <Dialog.Title className="text-display uppercase">
                  All Reviews for {capperName}
                </Dialog.Title>
                <Dialog.Close asChild>
                  <Button variant="circle" size="sm" color="tertiary">
                    <Icon name="close" size="sm" />
                  </Button>
                </Dialog.Close>
              </div>

              {/* Content */}
              <div className="flex-1 bg-surface-100 p-6 overflow-y-auto">
                {reviews.length > 0 ? (
                  <div className="gap-x-6 gap-y-8 grid grid-cols-1 md:grid-cols-2">
                    {reviews.map((review) => (
                      <CapperReviewCard
                        key={review.id}
                        reviewDetails={{
                          memberSince: formatMemberSince(
                            review.reviewer.created_at ||
                              new Date().toISOString()
                          ),
                          rating: review.rating,
                          review: review.comment,
                          timeAgo: formatTimeAgo(review.created_at),
                          reviewerName: review.reviewer.display_name,
                        }}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="py-12 text-center">
                    <p className="text-secondary-400 text-lg">
                      No reviews available for {capperName} yet.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </>
  );
}
